import {getProfileDescription} from '../../../appModules/utilities/metaDataUtilities';
import _ from 'lodash';
import {
  ensureProtocol,
  buildUrl,
} from '../../../reactModules/shared/utilities/urlHelpers';
import moment from 'moment';
import {formatTitle, sanitizeText} from '../../utilities/formatHelpers';
import appConfig from '../../../appConfig/AppConfig';

const conf = appConfig();

const DEFAULT_DATA = {
  '@context': 'http://schema.org',
  '@type': 'LocalBusiness',
  name: '',
  telephone: '',
  address: '',
  url: '',
  description: '',
  areaServed: '',
  foundingdate: '',
};

const DEFAULT_ADDRESS = {
  '@type': 'PostalAddress',
  addressLocality: '',
  addressRegion: '',
  streetAddress: '',
  postalCode: '',
};

const DEFAULT_EVENT = {
  '@context': 'http://www.schema.org',
  '@type': 'Event',
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  location: {
    '@type': 'Place',
    name: '',
    address: DEFAULT_ADDRESS,
  },
};

/**
 * Builds JSON LD Data string from a funeral home
 * @param {object} funeralHome Funeral Home object
 * @param {object} upcomingServices Upcoming Services at funeral home
 * @return {object} JSON LD String
 */
export function addFuneralHomeAndEventSchema(funeralHome, upcomingServices) {
  const {
    location = {},
    profileData,
    attributesData,
    logoImageUrl,
    primaryImageUrl,
  } = funeralHome;
  const {websiteUrl = ''} = profileData;
  const {city, state: fhState, postalCode} = location;
  const description =
    _.get(funeralHome, 'meta.description') ||
    getProfileDescription(funeralHome);
  const {people = []} = upcomingServices;
  const eventSchema = [];

  const address = _.defaults(
    {
      '@type': 'PostalAddress',
      addressLocality: city.fullName,
      addressRegion: fhState.fullName,
      streetAddress: profileData.address1,
      postalCode,
    },
    DEFAULT_ADDRESS
  );

  if (people.length > 0) {
    people.forEach(person => {
      const {services = []} = person;

      if (services.length > 0) {
        services.forEach(service => {
          const serviceDate = moment.utc(service.serviceDate);
          const serviceLocation = service.isAtFuneralHome
            ? {
                name: funeralHome.name,
                address,
              }
            : {
                name: service.name,
                address: {
                  addressLocality: _.get(service, 'location.city', ''),
                  addressRegion: _.get(service, 'location.state', ''),
                  streetAddress: _.get(service, 'location.address', ''),
                  postalCode: _.get(service, 'location.postalCode', ''),
                },
              };

          eventSchema.push(
            _.defaultsDeep(
              {
                '@context': 'http://www.schema.org',
                '@type': 'Event',
                name: `${person.fullName} - ${service.serviceType}`,
                description: `Join us for the ${
                  service.serviceType
                } to celebrate the life of ${
                  person.fullName
                } on the day of ${serviceDate.format('MMMM Do, YYYY')} at the ${
                  funeralHome.name
                }`,
                startDate: `${serviceDate.format('MM/DD/YYYY ')}`,
                endDate: `${serviceDate.format('MM/DD/YYYY')}`,
                location: {
                  '@type': 'Place',
                  ...serviceLocation,
                },
              },
              DEFAULT_EVENT
            )
          );
        });
      }
    });
  }

  const funeralHomeSchema = _.defaults(
    {
      name: funeralHome.name,
      telephone: funeralHome.phone,
      address,
      url: ensureProtocol(websiteUrl),
      description,
    },
    DEFAULT_DATA
  );

  if (primaryImageUrl) {
    funeralHomeSchema.image = primaryImageUrl;

    if (eventSchema.length > 0) {
      eventSchema.map(event => ({
        ...event,
        image: [primaryImageUrl],
      }));
    }
  }

  if (logoImageUrl) {
    funeralHomeSchema.logo = logoImageUrl;
  }

  // Remove any details with 'N/A' values
  // eslint-disable-next-line
  // TODO: This logic is duplicated down in the Details component
  const validDetails = attributesData.filter(
    item => item.value && !item.value.toLowerCase().includes('n/a')
  );

  const areaServed = validDetails.find(
    item => item.name === 'Communities Served'
  );

  const founded = validDetails.find(item => item.name === 'Founded Year');

  if (areaServed) {
    funeralHomeSchema.areaServed = areaServed.value.split(', ');
  }

  if (founded) {
    funeralHomeSchema.foundingdate = founded.value;
  }

  return {
    funeralHome: funeralHomeSchema,
    events: eventSchema,
  };
}

/**
 * get name
 * @param  {object} name name obj
 * @return {String} full name
 */
function getFullName(name) {
  const {full, short, first, last} = name;
  let personName = full ? full : short;

  if (!personName) {
    personName = [first, last]
      .filter(nameItem => {
        return nameItem;
      })
      .join(' ');
  }
  return personName;
}

/**
 * http://schema.org/newsarticle
 * @param {Object} person Person page data.
 * @return {Object} Returns formated object.
 */
export function addNewsArticleSchema(person) {
  const {
    portraitImage: {href: imgHref, width, height} = {},
    meta = {},
    name = {},
    displayText = {},
    location: {city, state} = {},
  } = person;
  const personUrl = meta.seoCanonicalUrl;
  const imgUrl = imgHref
    ? `${conf.environmentalUrl.cacheUrl}${imgHref}`
    : `${
        conf.environmentalUrl.staticCacheUrl
      }/obituaries/images/premiumobit/premiumobit_candle.jpg`;
  const imgWidth = imgHref && width ? width : 130;
  const imgHeight = imgHref && height ? height : 130;
  const personName = getFullName(name);
  const obitText = displayText.fullSanitized || displayText.text || '';
  const articleBody = sanitizeText(obitText);
  const logoUrl =
    'https://static-cms.legacy.com/sites/default/files/legacy-com-logo-a.png';
  const obitId = _.head(_.get(person, 'obitSources.person'));
  const obit = _.find(_.get(person, 'obituaries', []), ['sourceId', obitId]);
  const datePublished =
    _.get(obit, 'dateCreated') || _.get(meta, 'latestDate', '');
  const dateModified = _.get(meta, 'latestDate', datePublished);
  const headline = formatTitle(name.first, name.last, city, state);

  return {
    '@context': 'http://schema.org',
    '@type': 'NewsArticle',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': personUrl,
    },
    headline,
    description: meta.description,
    url: personUrl,
    image: {
      '@type': 'ImageObject',
      url: imgUrl,
      height: imgHeight,
      width: imgWidth,
    },
    articleSection: 'Obituaries',
    genre: 'Obituary(Obit),Biography',
    dateCreated: datePublished,
    datePublished,
    dateModified,
    articleBody,
    creator: ['Legacy.com'],
    author: {
      '@type': 'Person',
      name: 'Legacy',
    },
    keywords: [`${personName} Obituary`, 'Obituaries'],
    publisher: {
      '@type': 'Organization',
      name: 'Legacy',
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
        width: 640,
        height: 200,
      },
    },
  };
}

/**
 * Formats service data to the correct schema.
 * For schema testing: https://search.google.com/structured-data/testing-tool
 * @param {Array} personData Person page data.
 * @return {Array} Returns array of formated events.
 */
export function addPersonEventsSchema(personData) {
  const serviceData = _.get(personData, 'services', false);

  if (serviceData) {
    return serviceData.map(event => {
      const {
        serviceType,
        city,
        state,
        postalCode,
        address: streetAddress,
        startTime,
        endTime,
        location,
      } = event;
      const address = _.defaults(
        {
          '@type': 'PostalAddress',
          addressLocality: city,
          addressRegion: state,
          streetAddress,
          postalCode,
        },
        DEFAULT_ADDRESS
      );

      return {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: serviceType || '',
        startDate: startTime || '',
        endDate: endTime || '',
        location: {
          '@type': 'Place',
          name: location || '',
          address
        },
      };
    });
  }
  return [];
}

/**
 * Format person data to the correct schema. (https://schema.org/Person)
 * For schema testing: https://search.google.com/structured-data/testing-tool
 * @param {Object} personData Person page data.
 * @return {Object} Returns formated object.
 */
export function addPersonSchema(personData) {
  if (!personData) {
    return {};
  }

  const {
    dateOfBirth,
    dateOfDeath,
    name = {},
    location = {},
    portraitImage = {},
  } = personData;
  const {full, short, first, last} = name;
  const {city, state} = location;

  let personName = full ? full : short;

  if (!personName) {
    personName = [first, last].filter(nameItem => nameItem).join(' ');
  }

  const personLocation = [city, state]
    .filter(locationItem => locationItem)
    .join(' ');

  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    additionalName: personName,
    address: {
      '@type': 'PostalAddress',
      addressLocality: personLocation,
    },
    birthDate: dateOfBirth || '',
    deathDate: dateOfDeath || '',
    familyName: last || '',
    givenName: first || '',
    name: personName,
    url: personData['og:url'],
    image: portraitImage.href
      ? `${buildUrl(conf.environmentalUrl.cacheUrl, portraitImage.href)}`
      : '',
  };
}

/**
 * Formats service data to the correct schema.
 * For schema testing: https://search.google.com/structured-data/testing-tool
 * @param {object} personData Person page data.
 * @return {object} Returns array of formated events.
 */
export function addItemListSchema(personData) {
  const obituaryData = _.get(personData, 'obituaries', false);

  if (obituaryData.length > 1) {
    const itemList = [];

    obituaryData.map((obit, index) => {
      const {href} = obit;

      itemList.push({
        '@type': 'ListItem',
        position: index + 1,
        url: href,
      });
    });
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: itemList,
    };
  }
  return {};
}



// WEBPACK FOOTER //
// ./src/appModules/services/jsonld/JsonLd.js